import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Save } from "lucide-react";
import { API, formatApiError } from "@/contexts/AuthContext";

// Regression repair 2026-10-03 · Issue 8 (timezone validation crash).
// Replace free-text input with a curated IANA whitelist. Empty =
// unset = uses browser timezone for display (per the backend contract).
const TIMEZONES = [
  "Europe/Amsterdam", "Europe/Brussels", "Europe/Berlin", "Europe/Paris",
  "Europe/London", "Europe/Dublin", "Europe/Madrid", "Europe/Lisbon",
  "Europe/Rome", "Europe/Vienna", "Europe/Zurich", "Europe/Stockholm",
  "Europe/Oslo", "Europe/Copenhagen", "Europe/Helsinki", "Europe/Warsaw",
  "Europe/Prague", "Europe/Athens", "Europe/Istanbul", "Europe/Moscow",
  "UTC",
  "Africa/Cairo", "Africa/Johannesburg", "Africa/Lagos",
  "America/New_York", "America/Chicago", "America/Denver",
  "America/Los_Angeles", "America/Toronto", "America/Vancouver",
  "America/Sao_Paulo", "America/Mexico_City", "America/Buenos_Aires",
  "Asia/Dubai", "Asia/Jerusalem", "Asia/Karachi", "Asia/Kolkata",
  "Asia/Bangkok", "Asia/Singapore", "Asia/Hong_Kong", "Asia/Shanghai",
  "Asia/Tokyo", "Asia/Seoul",
  "Australia/Sydney", "Australia/Perth",
  "Pacific/Auckland",
];
const LANGUAGES = [
  ["en", "English"], ["nl", "Nederlands"], ["de", "Deutsch"],
  ["fr", "Français"], ["es", "Español"], ["it", "Italiano"], ["pt", "Português"],
];
const DATE_FORMATS = [
  ["iso", "ISO (2026-02-15)"], ["dmy", "DMY (15/02/2026)"], ["mdy", "MDY (02/15/2026)"],
];
const NUMBER_FORMATS = [
  "en-IE", "en-US", "en-GB",
  "nl-NL", "de-DE", "fr-FR", "es-ES", "it-IT", "pt-PT",
];
const CURRENCIES = [
  "EUR", "USD", "GBP", "CHF",
  "SEK", "NOK", "DKK", "PLN", "CZK",
  "AUD", "CAD", "JPY",
];

export default function PreferencesPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    language: "", timezone: "", date_format: "",
    number_format: "", currency: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/users/me/preferences`);
      setForm({
        language:      data.language      || "",
        timezone:      data.timezone      || "",
        date_format:   data.date_format   || "",
        number_format: data.number_format || "",
        currency:      data.currency      || "",
      });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not load preferences.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    // Send every field explicitly; "" clears back to null on the server.
    const body = {
      language:      form.language      || null,
      timezone:      form.timezone      || null,
      date_format:   form.date_format   || null,
      number_format: form.number_format || null,
      currency:      form.currency      || null,
    };
    try {
      await axios.patch(`${API}/users/me/preferences`, body);
      toast.success("Preferences saved.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="mt-6 bg-white border border-[#eee] rounded-2xl p-6" data-testid="preferences-panel-loading">
        <div className="flex items-center gap-2 text-[13.5px] text-[#666]">
          <Loader2 size={14} className="animate-spin" /> Loading preferences…
        </div>
      </section>
    );
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <section className="mt-6 bg-white border border-[#eee] rounded-2xl p-6" data-testid="preferences-panel">
      <h2 className="text-[15px] font-semibold mb-1">Preferences</h2>
      <p className="text-[13px] text-[#666] mb-5">
        Personal locale, timezone, and formatting. Any field left blank uses your browser's setting for display only.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Language">
          <select
            value={form.language}
            onChange={(e) => set("language", e.target.value)}
            className="w-full h-9 rounded-md border border-[#e0e0e0] px-2.5 text-[13.5px] bg-white"
            data-testid="preferences-language"
          >
            <option value="">— Unset (browser) —</option>
            {LANGUAGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
        <Field label="Timezone (IANA)">
          <select
            value={form.timezone}
            onChange={(e) => set("timezone", e.target.value)}
            className="w-full h-9 rounded-md border border-[#e0e0e0] px-2.5 text-[13.5px] bg-white"
            data-testid="preferences-timezone"
          >
            <option value="">— Unset (browser) —</option>
            {TIMEZONES.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </Field>
        <Field label="Date format">
          <select
            value={form.date_format}
            onChange={(e) => set("date_format", e.target.value)}
            className="w-full h-9 rounded-md border border-[#e0e0e0] px-2.5 text-[13.5px] bg-white"
            data-testid="preferences-date-format"
          >
            <option value="">— Unset —</option>
            {DATE_FORMATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
        <Field label="Number format">
          <select
            value={form.number_format}
            onChange={(e) => set("number_format", e.target.value)}
            className="w-full h-9 rounded-md border border-[#e0e0e0] px-2.5 text-[13.5px] bg-white"
            data-testid="preferences-number-format"
          >
            <option value="">— Unset —</option>
            {NUMBER_FORMATS.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </Field>
        <Field label="Currency">
          <select
            value={form.currency}
            onChange={(e) => set("currency", e.target.value)}
            className="w-full h-9 rounded-md border border-[#e0e0e0] px-2.5 text-[13.5px] bg-white"
            data-testid="preferences-currency"
          >
            <option value="">— Unset —</option>
            {CURRENCIES.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </Field>
      </div>
      <div className="pt-4">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="zy-btn-primary disabled:opacity-70"
          data-testid="preferences-save"
        >
          {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Save size={14} /> Save preferences</>}
        </button>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[12.5px] font-medium">{label}</Label>
      {children}
    </div>
  );
}
